import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Box,
  CircularProgress,
  FormControlLabel,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

const sortByCountryId = (items) =>
  [...items].sort((a, b) => Number(a.countryId) - Number(b.countryId));

const CustomerPaymentToDeltaSettingsPanel = ({ customerId, setNotify }) => {
  const [settings, setSettings] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [savingCountryId, setSavingCountryId] = useState(null);

  const sortedSettings = useMemo(() => sortByCountryId(settings), [settings]);

  const fetchSettings = useCallback(async () => {
    if (!customerId) {
      setSettings([]);
      return;
    }

    setIsLoading(true);

    try {
      const response = await axios.get(`/api/v1/entities/${customerId}/paymentToDeltaSettings`);
      setSettings(sortByCountryId(response.data || []));
    } catch (error) {
      console.error("Error loading payment to Delta settings:", error);
      setNotify({
        isOpen: true,
        message: "Failed to load payment to Delta settings.",
        type: "error",
      });
      setSettings([]);
    } finally {
      setIsLoading(false);
    }
  }, [customerId, setNotify]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleToggle = async (setting, checked) => {
    if (savingCountryId !== null) {
      return;
    }

    const previousSettings = settings;
    setSettings((currentSettings) =>
      currentSettings.map((currentSetting) =>
        currentSetting.countryId === setting.countryId
          ? { ...currentSetting, paymentToDeltaEnabled: checked }
          : currentSetting
      )
    );
    setSavingCountryId(setting.countryId);

    try {
      await axios.put(`/api/v1/entities/${customerId}/paymentToDeltaSettings/${setting.countryId}`, {
        paymentToDeltaEnabled: checked,
      });
      setNotify({
        isOpen: true,
        message: checked
          ? `Payment to Delta enabled for ${setting.countryName}`
          : `Payment to Delta disabled for ${setting.countryName}`,
        type: "success",
      });
    } catch (error) {
      console.error("Error saving payment to Delta setting:", error);
      setSettings(previousSettings);
      setNotify({
        isOpen: true,
        message: `Failed to save payment to Delta setting for ${setting.countryName}.`,
        type: "error",
      });
    } finally {
      setSavingCountryId(null);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
        Payment to Delta
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Enable payment via the Delta account per country. Only countries with Payment to Delta allowed in Global Settings are shown.
      </Typography>

      {isLoading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={28} />
        </Box>
      ) : sortedSettings.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No countries are configured for payment to Delta in Global Settings.
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Country Code</TableCell>
                <TableCell>Country Name</TableCell>
                <TableCell align="right">Payment to Delta account</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedSettings.map((setting) => (
                <TableRow key={setting.countryId}>
                  <TableCell>{setting.countryCode}</TableCell>
                  <TableCell>{setting.countryName}</TableCell>
                  <TableCell align="right">
                    <FormControlLabel
                      control={
                        <Switch
                          checked={Boolean(setting.paymentToDeltaEnabled)}
                          onChange={(event) => handleToggle(setting, event.target.checked)}
                          inputProps={{
                            "aria-label": `Payment to Delta account for ${setting.countryName}`,
                          }}
                        />
                      }
                      label=""
                      sx={{ m: 0 }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
};

export default CustomerPaymentToDeltaSettingsPanel;
